import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { fetchCarousel } from '@/services/api';
import { ChevronRight, Sparkles, MapPin, ArrowDown } from 'lucide-react';
import axios from 'axios';

interface CarouselItem {
  image: string;
  title: string;
  description: string;
}

const Hero = () => {
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [siteIcon, setSiteIcon] = useState<string | null>(null);

  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const response = await fetchCarousel();
        const data = response.data.carouselItems;
        const validItems = data.filter((item: any) => item.image && item.title && item.description);
        setCarouselItems(validItems);
      } catch (error) {
        console.error('Failed to load carousel items:', error);
      }
    };
    loadCarousel();
  }, []);

  useEffect(() => {
    const fetchSiteIcon = async () => {
      try {
        const response = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/site_icon');
        setSiteIcon(response.data);
      } catch (error) {
        console.error('Failed to fetch site icon', error);
      }
    };
    fetchSiteIcon();
  }, []);

  useEffect(() => {
    if (carouselItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [carouselItems]);

  const current = carouselItems[currentSlide];
  const imageUrl = current?.image.startsWith('http')
    ? current.image
    : `https://api.ibadansouthanglicandiocese.org.ng/img/${current?.image}`;

  return (
    <section className="relative h-[85vh] lg:h-screen min-h-[700px] flex items-center overflow-hidden bg-slate-950">
      
      {/* Background Layer - Optimized for PC Performance */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0 will-change-transform"
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform"
              style={{ 
                backgroundImage: `url('${imageUrl}')`,
                transform: 'translateZ(0)' // Forces GPU rendering on PC
              }}
            />
            {/* Multi-stage Overlay for Readability */}
            <div className="absolute inset-0 bg-slate-950/60" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center w-full">

        {/* Left Content Area */}
        <div className="lg:col-span-8 xl:col-span-7 space-y-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-full backdrop-blur-md"
          >
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 text-[11px] font-black uppercase tracking-[0.4em]">Anglican Communion</span>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.8, ease: "circOut" }}
              className="space-y-6"
            >
              <h1 className="text-5xl md:text-6xl lg:text-[6rem] font-serif font-bold text-white leading-[0.95] tracking-tighter">
                {current?.title || "Welcome to Ibadan South"}
              </h1>
              <p className="text-lg lg:text-2xl text-slate-300 font-serif italic max-w-2xl leading-relaxed opacity-90">
                {current?.description || "Building God's kingdom through faith, fellowship, and service."}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex flex-wrap gap-5 pt-4">
            <Button asChild size="lg" className="h-12 px-6 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-widest text-[11px] rounded-2xl transition-all hover:scale-105 shadow-xl shadow-amber-500/20">
              <Link to="/about">Our Journey <ChevronRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-6 border-white/20 bg-white/5 backdrop-blur-xl text-white hover:bg-white hover:text-slate-950 font-black uppercase tracking-widest text-[11px] rounded-2xl transition-all">
              <Link to="/events">Join Us This Sunday</Link>
            </Button>
          </div>
        </div>

        {/* Right Decorative Area - Refined for PC */}
        <div className="lg:col-span-4 xl:col-span-5 hidden lg:flex justify-end relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="relative"
          >
            <div className="w-80 h-[28rem] bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[4rem] p-10 flex flex-col items-center justify-center text-center shadow-3xl relative z-10 group hover:border-amber-500/30 transition-colors duration-500">
              <div className="w-24 h-24 bg-amber-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-amber-500/40 mb-8 group-hover:rotate-12 transition-transform duration-700">
                {siteIcon ? (
                  <img src={siteIcon} alt="Icon" className="w-14 h-14 object-contain" />
                ) : (
                  <span className="text-5xl text-slate-950 font-bold">✝</span>
                )}
              </div>
              <h3 className="text-white font-serif text-3xl font-bold mb-6 italic leading-tight">"The Joy of the Lord"</h3>
              <div className="h-px w-16 bg-amber-500/50 mb-6" />
              <p className="text-amber-500 font-black uppercase tracking-[0.3em] text-[10px]">Our Strength</p>

              <div className="mt-16 flex items-center gap-3 text-slate-400 text-[10px] uppercase font-black tracking-widest">
                <MapPin size={14} className="text-amber-500" />
                <span>Ibadan, Nigeria</span>
              </div>
            </div>

            {/* Decorative Rings with pulse */}
            <motion.div 
              animate={{ scale: [1, 1.05, 1], opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[130%] border border-white/10 rounded-full z-0" 
            />
          </motion.div>
        </div>
      </div>

      {/* Slide Navigation Indicators */}
      <div className="absolute bottom-12 left-6 lg:left-12 flex items-center gap-4 z-20 bg-black/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/5">
        {carouselItems.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className="group relative h-8 w-8 flex items-center justify-center"
          >
            <div className={`h-1 transition-all duration-500 rounded-full ${currentSlide === idx ? 'w-8 bg-amber-500' : 'w-4 bg-white/20 group-hover:bg-white/40'}`} />
          </button>
        ))}
      </div>

      {/* Scroll Down Hint */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 right-12 z-20 text-white/20 hidden lg:flex flex-col items-center gap-4"
      >
        <span className="[writing-mode:vertical-lr] uppercase tracking-[0.5em] text-[9px] font-bold">Scroll</span>
        <div className="h-12 w-px bg-gradient-to-b from-white/20 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;